import { z } from 'zod';
import type {
  CallEventEnvelopeV2,
  CallSnapshotV2,
} from '../api/generated/calls-v2';

export type {
  CallActionNameV2,
  CallEventEnvelopeV2,
  CallMediaStateV2,
  CallMediaTopologyV2,
  CallParticipantStatusV2,
  CallParticipantV2,
  CallSessionStatusV2,
  CallSnapshotV2,
  CallSyncV2,
} from '../api/generated/calls-v2';

export type CallLifecycleStatus = 'ringing' | 'active' | 'ended' | 'declined' | 'cancelled' | 'missed';
export type CallMediaTopology = 'p2p' | 'sfu';
export type CallLifecycleAction =
  | 'answer' | 'decline' | 'cancel' | 'end' | 'participant-joined' | 'participant-left'
  | 'accept' | 'join' | 'leave' | 'busy'
  | 'media-connecting' | 'media-connected' | 'media-reconnecting' | 'media-disconnected';

export interface CallSnapshot {
  callId: string;
  conversationId: string;
  initiatorUserId: string;
  participantUserIds: string[];
  isGroup: boolean;
  mediaTopology: CallMediaTopology;
  status: CallLifecycleStatus;
  version: number;
  eventId: string;
  startedAt: string;
  answeredAt: string | null;
  acceptedAt?: string | null;
  endedAt: string | null;
  expiresAt: string;
  serverTimestamp?: string | null;
  reason?: string | null;
}

/** Compatibility projection for older providers while the wire contract is v2. */
export function callSnapshotV2ToLegacy(snapshot: CallSnapshotV2): CallSnapshot {
  const accepted = snapshot.participants.find(item =>
    item.status === 'accepted' || item.status === 'joined'
  )?.acceptedAt ?? null;
  const status: CallLifecycleStatus = snapshot.status === 'expired'
    ? 'missed'
    : snapshot.status === 'failed'
      ? 'ended'
      : snapshot.status;
  return {
    callId: snapshot.callId,
    conversationId: snapshot.conversationId,
    initiatorUserId: snapshot.initiatorUserId,
    participantUserIds: snapshot.participants.map(item => item.userId),
    isGroup: snapshot.mediaTopology === 'sfu' || snapshot.participants.length > 2,
    mediaTopology: snapshot.mediaTopology,
    status,
    version: snapshot.version,
    eventId: snapshot.eventId,
    startedAt: snapshot.serverTimestamp,
    answeredAt: accepted,
    acceptedAt: accepted,
    endedAt: snapshot.endedAt,
    expiresAt: snapshot.expiresAt ?? '',
    serverTimestamp: snapshot.serverTimestamp,
    reason: snapshot.reason,
  };
}

export function normaliseCallSnapshot(value: CallSnapshot | CallSnapshotV2): CallSnapshot {
  return 'schemaVersion' in value && value.schemaVersion === 2
    ? callSnapshotV2ToLegacy(value)
    : value;
}

export interface CallEventEnvelope {
  type: 'call-state';
  eventType: 'call-state';
  event: string;
  callId: string;
  eventId: string;
  senderUserId: string | null;
  sequenceNumber: number;
  serverTimestamp: string;
  snapshot: CallSnapshot;
}

export function callEventEnvelopeV2ToLegacy(value: CallEventEnvelopeV2): CallEventEnvelope {
  return {
    type: 'call-state',
    eventType: 'call-state',
    event: value.eventType,
    callId: value.callId,
    eventId: value.eventId,
    senderUserId: value.actorUserId,
    sequenceNumber: value.sequenceNumber,
    serverTimestamp: value.serverTimestamp,
    snapshot: callSnapshotV2ToLegacy(value.snapshot),
  };
}

const callParticipantV2Schema = z.object({
  userId: z.string().min(1),
  status: z.enum(['invited', 'ringing', 'accepted', 'joined', 'declined', 'left', 'busy']),
  invitedAt: z.string().min(1),
  acceptedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  leftAt: z.string().nullable(),
  mediaState: z.enum(['connecting', 'connected', 'reconnecting', 'disconnected']),
}).strict();

const callSnapshotV2Schema = z.object({
  schemaVersion: z.literal(2),
  callId: z.string().min(1),
  conversationId: z.string().min(1),
  initiatorUserId: z.string().min(1),
  moderatorUserIds: z.array(z.string()),
  mediaKind: z.literal('audio'),
  mediaTopology: z.enum(['p2p', 'sfu']),
  status: z.enum(['ringing', 'active', 'ended', 'cancelled', 'expired', 'failed']),
  mediaState: z.enum(['connecting', 'connected', 'reconnecting', 'disconnected']),
  version: z.number().int().min(1),
  eventId: z.string().min(1),
  serverTimestamp: z.string().min(1),
  expiresAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  reason: z.string().nullable(),
  participants: z.array(callParticipantV2Schema).min(1),
}).strict().superRefine((value, ctx) => {
  const participantIds = value.participants.map(item => item.userId);
  if (new Set(participantIds).size !== participantIds.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'participants must be unique', path: ['participants'] });
  }
  if (!participantIds.includes(value.initiatorUserId)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'initiator must be a participant', path: ['initiatorUserId'] });
  }
  if (value.moderatorUserIds.some(userId => !participantIds.includes(userId))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'moderators must be participants', path: ['moderatorUserIds'] });
  }
});

const callEventEnvelopeV2Schema = z.object({
  schemaVersion: z.literal(2),
  type: z.literal('call-state'),
  eventType: z.enum(['session-created', 'session-updated', 'participant-updated']),
  callId: z.string().min(1),
  eventId: z.string().min(1),
  sequenceNumber: z.number().int().min(1),
  serverTimestamp: z.string().min(1),
  actorUserId: z.string().nullable(),
  participant: callParticipantV2Schema.nullable(),
  snapshot: callSnapshotV2Schema,
}).strict().superRefine((value, ctx) => {
  if (value.eventType === 'participant-updated' && !value.participant) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'participant-updated events require participant state', path: ['participant'] });
  }
  if (value.callId !== value.snapshot.callId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'callId mismatch', path: ['callId'] });
  }
  if (value.eventId !== value.snapshot.eventId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'eventId mismatch', path: ['eventId'] });
  }
  if (value.sequenceNumber !== value.snapshot.version) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'sequence mismatch', path: ['sequenceNumber'] });
  }
  if (value.serverTimestamp !== value.snapshot.serverTimestamp) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'timestamp mismatch', path: ['serverTimestamp'] });
  }
  if (value.participant) {
    const snapshotParticipant = value.snapshot.participants.find(item => item.userId === value.participant?.userId);
    if (!snapshotParticipant || JSON.stringify(snapshotParticipant) !== JSON.stringify(value.participant)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'participant mismatch', path: ['participant'] });
    }
  }
});

export function parseCallSnapshotV2(value: unknown): CallSnapshotV2 {
  return callSnapshotV2Schema.parse(value) as CallSnapshotV2;
}

export function isCallEventEnvelopeV2(value: unknown): value is CallEventEnvelopeV2 {
  return callEventEnvelopeV2Schema.safeParse(value).success;
}

export interface CallStartInput {
  clientEventId: string;
  conversationId: string;
  participantUserIds: string[];
  callType?: 'voice';
}

export interface CallActionInput {
  action: CallLifecycleAction;
  eventId: string;
  expectedVersion: number;
}

export interface AddCallParticipantsInput {
  eventId: string;
  expectedVersion: number;
  participantUserIds: string[];
}

export interface CallSyncResponse {
  calls: CallSnapshot[];
}

export interface P2pOffer {
  type: 'call-invite';
  call_id: string;
  conversation_id: string;
  from_user_id: string;
  from_name?: string | null;
  from_avatar_url?: string | null;
  media?: 'audio';
  sdp: RTCSessionDescriptionInit;
}

export interface LiveKitCredentials {
  callId: string;
  roomName: string;
  serverUrl: string;
  participantToken: string;
  expiresAt: string;
}

const CALL_STATUSES = new Set<CallLifecycleStatus>([
  'ringing', 'active', 'ended', 'declined', 'cancelled', 'missed',
]);
const MEDIA_TOPOLOGIES = new Set<CallMediaTopology>(['p2p', 'sfu']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function isCallStateEnvelope(value: unknown): value is CallEventEnvelope {
  if (!isRecord(value) || value.type !== 'call-state' || value.eventType !== 'call-state') return false;
  if (
    typeof value.callId !== 'string'
    || typeof value.eventId !== 'string'
    || typeof value.event !== 'string'
    || typeof value.sequenceNumber !== 'number'
    || typeof value.serverTimestamp !== 'string'
    || !isRecord(value.snapshot)
  ) return false;

  const snapshot = value.snapshot;
  return snapshot.callId === value.callId
    && typeof snapshot.callId === 'string'
    && typeof snapshot.conversationId === 'string'
    && typeof snapshot.initiatorUserId === 'string'
    && isStringArray(snapshot.participantUserIds)
    && typeof snapshot.isGroup === 'boolean'
    && MEDIA_TOPOLOGIES.has(snapshot.mediaTopology as CallMediaTopology)
    && CALL_STATUSES.has(snapshot.status as CallLifecycleStatus)
    && Number.isInteger(snapshot.version)
    && snapshot.version >= 1
    && typeof snapshot.eventId === 'string';
}
